import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Flex,
  Text,
  Image,
  useColorModeValue,
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useStore } from "@nanostores/react";
import { userProfile } from "contexts/userProfile";
import { FaHandSparkles, FaUpload } from "react-icons/fa";
import { setDoc, doc } from "@firebase/firestore";
import { db } from "lib/firebase";
import Lottie from "lottie-react";
import groovyWalkAnimation from "assets/animations/upload.json";

function UploadDocument() {
  const [loading, setLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { data, documents } = useStore(userProfile);
  const { counts } = documents;

  const fileInputRef = useRef(null);
  const history = useHistory();
  const [selectedFile, setSelectedFile] = useState(null);
  const textColor = useColorModeValue("gray.700", "white");

  const saveUserProfileToFirestore = async () => {
    if (!data) {
      console.error("User profile is not available.");
      return;
    }

    const userData = {
      uid: data.uid,
      displayName: data.displayName,
      email: data.email,
    };

    const userRef = doc(db, "users", data.uid);

    try {
      await setDoc(userRef, userData);
    } catch (error) {
      console.error("Error storing user profile: ", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      console.log("Loading set to true");
      setLoading(true);

      setSelectedFile(file);

      setTimeout(() => {
        setLoading(false);
        setShouldRedirect(true); // Add this
      }, 2000);
    } else {
      toast.error("Please upload a valid PDF file.");
    }
  };

  const handleFileDrop = (file) => {
    if (file.type === "application/pdf") {
      setLoading(true);
      setSelectedFile(file);
      setTimeout(() => {
        setLoading(false);
        setShouldRedirect(true); // Add this
      }, 2000);
    } else {
      toast.error("Please drop a valid PDF file.");
    }
  };

  useEffect(() => {
    saveUserProfileToFirestore();

    if (shouldRedirect && selectedFile) {
      history.push("/admin/add-signer", { selectedDocument: selectedFile });
    }
  }, [shouldRedirect, selectedFile, history]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px", lg: "100px" }}>
      <Text fontSize="2xl" fontWeight="bold" color="gray.200" ml={2} mb={2}>
        Hello, {data?.displayName}!
      </Text>
      <SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing="24px" mb="20px">
        {/* Pending signature Card */}
        <Card minH="70px" backgroundColor="rgba(255,255,255, 0.9)">
          <Flex direction="column" align="center" justify="center" w="100%" mb="25px">
            <Stat me="auto">
              <StatLabel fontSize="sm" color="gray.500" fontWeight="bold" textTransform="uppercase">
                <Box
                  as="span"
                  borderRadius="50%"
                  w="8px"
                  h="8px"
                  bg="orange.400"
                  mr="2"
                  display="inline-block"
                />
                Pending signature
              </StatLabel>
              <Flex>
                <StatNumber fontSize="2xl" color={textColor} fontWeight="bold">
                  {counts?.pendingSignature || 0}
                </StatNumber>
              </Flex>
            </Stat>
          </Flex>
        </Card>

        {/* Pending your signature Card */}
        <Card minH="70px" backgroundColor="rgba(255,255,255, 0.9)">
          <Flex direction="column" align="center" justify="center" w="100%" mb="25px">
            <Stat me="auto">
              <StatLabel fontSize="sm" color="gray.500" fontWeight="bold" textTransform="uppercase">
                <Box
                  as="span"
                  borderRadius="50%"
                  w="8px"
                  h="8px"
                  bg="orange.400"
                  mr="2"
                  display="inline-block"
                />
                Pending your signature
              </StatLabel>
              <Flex>
                <StatNumber fontSize="2xl" color={textColor} fontWeight="bold">
                  {counts?.pendingYourSignature || 0}
                </StatNumber>
              </Flex>
            </Stat>
          </Flex>
        </Card>

        {/* Draft Card */}
        <Card minH="70px" backgroundColor="rgba(255,255,255, 0.9)">
          <Flex direction="column" align="center" justify="center" w="100%" mb="25px">
            <Stat me="auto">
              <StatLabel fontSize="sm" color="gray.500" fontWeight="bold" textTransform="uppercase">
                <Box
                  as="span"
                  borderRadius="50%"
                  w="8px"
                  h="8px"
                  bg="gray.400"
                  mr="2"
                  display="inline-block"
                />
                Pending Review
              </StatLabel>
              <Flex>
                <StatNumber fontSize="2xl" color={textColor} fontWeight="bold">
                  {counts?.pendingInsights || 0}
                </StatNumber>
              </Flex>
            </Stat>
          </Flex>
        </Card>

        {/* Signed Card */}
        <Card minH="70px" backgroundColor="rgba(255,255,255, 0.9)">
          <Flex direction="column" align="center" justify="center" w="100%" mb="25px">
            <Stat me="auto">
              <StatLabel fontSize="sm" color="gray.500" fontWeight="bold" textTransform="uppercase">
                <Box
                  as="span"
                  borderRadius="50%"
                  w="8px"
                  h="8px"
                  bg="green.400"
                  mr="2"
                  display="inline-block"
                />
                Signed
              </StatLabel>
              <Flex>
                <StatNumber fontSize="2xl" color={textColor} fontWeight="bold">
                  {counts?.signedCard || 0}
                </StatNumber>
              </Flex>
            </Stat>
          </Flex>
        </Card>
      </SimpleGrid>

      <Box
        mt={6}
        p={4}
        backgroundColor="white"
        borderRadius="15px"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
      >
        <Flex
          justifyContent="center"
          p="24px"
          borderWidth="2px"
          borderColor="gray.500"
          borderStyle="dashed"
          height="calc(25vh - 8px)"
          width="calc(100% - 8px)"
          alignItems="center"
          flexDirection="column"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              handleFileDrop(e.dataTransfer.files[0]);
            }
          }}
        >
          {loading ? (
            <Lottie animationData={groovyWalkAnimation} style={{ height: 200, width: 200 }} />
          ) : (
            <>
              <Box as={FaUpload} size="24px" color="gray.500" mb="8px" />
              <Text color="gray.500" mb="8px">
                Drag & Drop your file here
              </Text>
              <Flex gap="1em">
                <Button
                  mt={4}
                  onClick={() => {
                    fileInputRef.current.click();
                  }}
                  _hover={{ color: "white", bgColor: "navy.400" }}
                >
                  Upload
                </Button>
                <Button
                  mt={4}
                  rightIcon={<FaHandSparkles />}
                  onClick={() => {
                    history.push("/admin/generate");
                  }}
                  _hover={{ color: "white", bgColor: "navy.400" }}
                >
                  Generate with AI
                </Button>
              </Flex>
            </>
          )}
        </Flex>

        <input
          type="file"
          accept=".pdf"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Box>

      <ToastContainer />
    </Flex>
  );
}

export default UploadDocument;
